import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PrintIcon from '@mui/icons-material/Print';
import {
  DEFAULT_GROUP_BEST_OF,
  buildAllRefereeForms,
  buildScoreSheets,
} from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';
import { printRefereeForms, printScoreSheets } from './printScoreSheet';
import { PoolMatrix } from './PoolMatrix';

interface GroupStageViewProps {
  onViewBracket: () => void;
}

export function GroupStageView({ onViewBracket }: GroupStageViewProps) {
  const { tournament } = useTournament();
  const { t, locale } = useTranslation();
  const groupOrder = tournament?.groupOrder;
  const config = tournament?.config;
  const timeSlotCount = tournament?.timeSlotCount ?? 0;
  const qualified = useMemo(() => {
    const slots = tournament?.slots;
    if (!slots) return { filled: 0, total: 0 };
    const values = Object.values(slots);
    return {
      filled: values.filter((slot) => slot.playerId !== null).length,
      total: values.length,
    };
  }, [tournament]);

  if (!groupOrder || !config) return null;

  const isFootball = tournament?.sportId === 'FOOTBALL';

  const handlePrint = () => {
    if (!tournament) return;
    // Football prints one referee match-report form per match (all groups,
    // plus any bracket match whose teams are known); table tennis prints the
    // per-group fill-in score sheets.
    if (isFootball) {
      printRefereeForms(buildAllRefereeForms(tournament), locale);
    } else {
      printScoreSheets(buildScoreSheets(tournament), locale);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('admin.groups.title')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            variant="outlined"
            label={
              isFootball
                ? t('admin.groups.chips.teams', { count: config.totalPlayers })
                : t('admin.groups.chips.players', { count: config.totalPlayers })
            }
          />
          <Chip
            variant="outlined"
            label={
              isFootball
                ? t('admin.groups.chips.pitches', { count: config.availableTables })
                : t('admin.groups.chips.tables', { count: config.availableTables })
            }
          />
          <Chip
            variant="outlined"
            label={t('admin.groups.chips.timeSlots', { count: timeSlotCount })}
          />
          {!isFootball && (
            <Chip
              variant="outlined"
              label={t('admin.groups.chips.bestOf', {
                bestOf: config.groupBestOf ?? DEFAULT_GROUP_BEST_OF,
              })}
            />
          )}
          <Chip
            color={qualified.filled === qualified.total ? 'success' : 'default'}
            label={t('admin.groups.chips.qualified', {
              filled: qualified.filled,
              total: qualified.total,
            })}
          />
        </Stack>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          {isFootball
            ? t('admin.groups.printAllRefereeForms')
            : t('admin.groups.printScoreSheets')}
        </Button>
        <Button
          variant="contained"
          startIcon={<AccountTreeIcon />}
          onClick={onViewBracket}
        >
          {t('admin.groups.viewBracket')}
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          // Cap at 3 groups per row; step down to 2 / 1 on narrower viewports
          // so cards stay wide enough for real player names.
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {groupOrder.map((poolId) => (
          <PoolMatrix key={poolId} poolId={poolId} />
        ))}
      </Box>
    </Box>
  );
}
